import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useTenantAuth } from './TenantAuthContext';
import { tenantSettingsService } from '../services/tenantSettingsService';

const DEFAULT_CURRENCY = 'KES';

const defaultFormatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: DEFAULT_CURRENCY,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);

interface TenantSettingsContextType {
  currency: string;
  setCurrency: (currency: string) => void;
  formatCurrency: (amount: number) => string;
  loadSettings: () => Promise<void>;
}

const defaultContextValue: TenantSettingsContextType = {
  currency: DEFAULT_CURRENCY,
  setCurrency: () => {},
  formatCurrency: defaultFormatCurrency,
  loadSettings: async () => {},
};

const TenantSettingsContext = createContext<TenantSettingsContextType>(defaultContextValue);

export const useTenantSettings = () => useContext(TenantSettingsContext);

/** Use when component may render outside TenantSettingsProvider (e.g. deposit page). Returns undefined when not in provider. */
export const useTenantSettingsOptional = () => useContext(TenantSettingsContext);

interface TenantSettingsProviderProps {
  children: React.ReactNode;
}

export const TenantSettingsProvider: React.FC<TenantSettingsProviderProps> = ({ children }) => {
  const { tenant } = useTenantAuth();
  const [currency, setCurrencyState] = useState<string>(DEFAULT_CURRENCY);

  const loadSettings = useCallback(async () => {
    if (!tenant?.id) return;
    try {
      const settings = await tenantSettingsService.getSettings(tenant.id);
      const c = settings.systemPreferences?.currency ?? DEFAULT_CURRENCY;
      setCurrencyState(c);
    } catch {
      setCurrencyState(DEFAULT_CURRENCY);
    }
  }, [tenant?.id]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const setCurrency = useCallback((c: string) => {
    setCurrencyState(c);
  }, []);

  const formatCurrency = useCallback((amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  }, [currency]);

  const value: TenantSettingsContextType = {
    currency,
    setCurrency,
    formatCurrency,
    loadSettings,
  };

  return (
    <TenantSettingsContext.Provider value={value}>
      {children}
    </TenantSettingsContext.Provider>
  );
};
